
import React from 'react';
import { formatCurrency } from '../../utils/calculator';
import { InfoTooltip } from '../../ui/InfoTooltip';

interface AssetBreakdownProps {
  formData: any;
  results: any;
  isUsingLoan: boolean;
}

export function AssetBreakdown({ formData, results, isUsingLoan }: AssetBreakdownProps) {
  const assetExemption = formData.applicantStatus === 'single' ? '€36,000' : '€72,000';

  return (
    <div className="bg-white rounded-lg border overflow-hidden">
      <div className="bg-grandpal-green/10 p-4 border-b">
        <h4 className="font-semibold text-grandpal-green">Asset Contribution</h4>
      </div>
      <div className="p-5 space-y-4">
        <div>
          <p className="text-sm mb-2">Under Fair Deal, the first {assetExemption} of assets is exempt.</p>
          
          <div className="space-y-3">
            {(formData.savingsAndCash > 0 || formData.investments > 0 || formData.otherAssets > 0) && (
              <div>
                <p className="font-medium mb-2">Cash Assets</p>
                <div className="grid grid-cols-2 gap-3 pl-4">
                  {formData.savingsAndCash > 0 && (
                    <div className="flex justify-between">
                      <p className="text-sm text-grandpal-gray">Savings & Cash</p>
                      <p className="text-sm">{formatCurrency(formData.savingsAndCash)}</p>
                    </div>
                  )}
                  {formData.investments > 0 && (
                    <div className="flex justify-between">
                      <p className="text-sm text-grandpal-gray">Investments</p>
                      <p className="text-sm">{formatCurrency(formData.investments)}</p>
                    </div>
                  )}
                  {formData.otherAssets > 0 && (
                    <div className="flex justify-between">
                      <p className="text-sm text-grandpal-gray">Other Cash Assets</p>
                      <p className="text-sm">{formatCurrency(formData.otherAssets)}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {formData.ownsPrimaryResidence && (
              <div>
                <div className="flex items-center gap-1">
                  <p className="font-medium mb-2">Primary Residence</p>
                  <InfoTooltip content="Subject to the 3-year cap (max contribution of 22.5% of value)." />
                </div>
                <p className="text-sm pl-4">{formatCurrency(formData.primaryResidenceValue)}</p>
              </div>
            )}
            
            {formData.hasOtherProperties && (
              <div>
                <div className="flex items-center gap-1">
                  <p className="font-medium mb-2">Other Properties</p>
                  <InfoTooltip content="Assessed indefinitely (no 3-year cap)." />
                </div>
                <p className="text-sm pl-4">{formatCurrency(formData.otherPropertiesValue)}</p>
              </div>
            )}
            
            {formData.hasFarmOrBusiness && (
              <div>
                <div className="flex items-center gap-1">
                  <p className="font-medium mb-2">Farm/Business</p>
                  {formData.familySuccessor ? (
                    <InfoTooltip content="Subject to the 3-year cap as you have a family successor." />
                  ) : (
                    <InfoTooltip content="Assessed indefinitely as no family successor selected." />
                  )}
                </div>
                <p className="text-sm pl-4">{formatCurrency(formData.farmBusinessValue)}</p>
              </div>
            )}
          </div>
        </div>
        
        <div className="border-t pt-4">
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center gap-1">
              <p className="font-medium">
                {formData.applicantStatus === 'single' 
                  ? "7.5% of Assets" 
                  : "3.75% of Combined Assets"}
              </p>
              <InfoTooltip content={
                formData.applicantStatus === 'single'
                  ? "After the asset exemption, 7.5% of remaining asset value is assessed annually."
                  : "As a couple, 3.75% of combined assets is assessed (equivalent to 7.5% of half)."
              } />
            </div>
            <p className="font-semibold text-grandpal-green">{formatCurrency(results.assetContribution.annual)}/year</p>
          </div>
          
          <div className="space-y-2 pl-4">
            {results.assetContribution.cash > 0 && (
              <div className="flex justify-between text-sm">
                <p>From cash assets</p>
                <p>{formatCurrency(results.assetContribution.cash * 52)}/year</p>
              </div>
            )}
            {results.assetContribution.primaryResidence > 0 && (
              <div className="flex justify-between text-sm">
                <p className="flex items-center gap-1">
                  From primary residence
                  <InfoTooltip content="Subject to 3-year cap" />
                </p>
                <p>{formatCurrency(results.assetContribution.primaryResidence * 52)}/year</p>
              </div>
            )}
            {results.assetContribution.otherProperties > 0 && (
              <div className="flex justify-between text-sm">
                <p>From other properties</p>
                <p>{formatCurrency(results.assetContribution.otherProperties * 52)}/year</p>
              </div>
            )}
            {results.assetContribution.farmBusiness > 0 && (
              <div className="flex justify-between text-sm">
                <p>From farm/business</p>
                <p>{formatCurrency(results.assetContribution.farmBusiness * 52)}/year</p>
              </div>
            )}
          </div>
        </div>
        
        <div className="border-t pt-4 flex justify-between items-center">
          <div className="flex items-center gap-1">
            <p className="font-medium">Weekly Asset Contribution</p>
          </div>
          <p className="font-semibold text-grandpal-green">{formatCurrency(results.assetContribution.weekly)}/week</p>
        </div>
        
        {isUsingLoan && (
          <div className="border-t pt-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-1">
                <p className="font-medium text-amber-700">
                  Deferred via Nursing Home Loan
                  <InfoTooltip content="This portion of your asset contribution will be deferred and repaid later from your estate." />
                </p>
              </div>
              <p className="font-medium text-amber-700">
                {formatCurrency(results.deferredAmount)}/week
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
